/**
 * Notes & Tasks — client API layer.
 *
 * Talks to server/api/notesTasks.ts (Admin SDK, bypasses Firestore rules —
 * see that file's header comment for why: relay.git's firestore.rules
 * doesn't reliably deploy to production). No client-side Firestore access
 * for this feature — fetch once on panel open, apply optimistic local
 * updates, persist via these calls.
 */

import { getAuthHeader } from "./auth";

export type Priority = "high" | "medium" | "low";

export interface TaskCategory {
  id: string;
  name: string;
  color: string;
  order: number;
  collapsed: boolean;
  createdAt: number | null;
}

export interface TaskItem {
  id: string;
  text: string;
  catId: string;
  priority: Priority;
  done: boolean;
  order: number;
  createdAt: number | null;
}

export interface NoteCategory {
  id: string;
  name: string;
  color: string;
  createdAt: number | null;
}

export interface Note {
  id: string;
  title: string;
  body: string;
  catId: string | null;
  pinned: boolean;
  createdAt: number | null;
  updatedAt: number | null;
}

export interface NotesTasksData {
  taskCategories: TaskCategory[];
  taskItems: TaskItem[];
  noteCategories: NoteCategory[];
  notes: Note[];
}

const BASE = "/api/notes-tasks";

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await getAuthHeader()),
  };
  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(body?.error || `Notes & Tasks request failed (${res.status})`);
  }
  return body as T;
}

export function fetchNotesTasksData(): Promise<NotesTasksData> {
  return req<NotesTasksData>("");
}

/* Task categories */
export function createTaskCategory(input: { name: string; color: string }): Promise<TaskCategory> {
  return req<TaskCategory>("/task-categories", { method: "POST", body: JSON.stringify(input) });
}
export function updateTaskCategory(id: string, changes: Partial<Pick<TaskCategory, "name" | "color" | "collapsed" | "order">>): Promise<void> {
  return req<void>(`/task-categories/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
}
export function deleteTaskCategory(id: string): Promise<void> {
  return req<void>(`/task-categories/${id}`, { method: "DELETE" });
}

/* Task items */
export function createTaskItem(input: { text: string; catId: string; priority?: Priority }): Promise<TaskItem> {
  return req<TaskItem>("/task-items", { method: "POST", body: JSON.stringify(input) });
}
export function updateTaskItem(id: string, changes: Partial<Pick<TaskItem, "text" | "done" | "priority" | "order" | "catId">>): Promise<void> {
  return req<void>(`/task-items/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
}
export function deleteTaskItem(id: string): Promise<void> {
  return req<void>(`/task-items/${id}`, { method: "DELETE" });
}

/**
 * Reorder items within a category via midpoint float insertion — mirrors
 * the pattern from the original notesTasksService.js handoff, adapted to
 * hit updateTaskItem instead of writing to Firestore directly. Pass the
 * full ordered array of items (same category) after a drag-drop.
 *
 * Returns the computed order value synchronously (the persisted write
 * happens in the background) so the caller can apply it to local state
 * immediately — the displayed 1/2/3 task numbers are derived by sorting on
 * `order`, so local state needs the real value right away, not just after
 * the PATCH round-trips.
 */
export function reorderTaskItem(orderedItems: TaskItem[], movedIndex: number): number {
  const prev = orderedItems[movedIndex - 1]?.order ?? 0;
  const next = orderedItems[movedIndex + 1]?.order ?? Date.now() * 2;
  const newOrder = next - prev < 0.001 ? (prev + next) / 2 + Math.random() * 0.0001 : (prev + next) / 2;
  updateTaskItem(orderedItems[movedIndex].id, { order: newOrder }).catch((e) => console.error("[notes-tasks] reorderTaskItem persist failed:", e));
  return newOrder;
}

/* Note categories */
export function createNoteCategory(input: { name: string; color: string }): Promise<NoteCategory> {
  return req<NoteCategory>("/note-categories", { method: "POST", body: JSON.stringify(input) });
}
export function deleteNoteCategory(id: string): Promise<void> {
  return req<void>(`/note-categories/${id}`, { method: "DELETE" });
}

/* Notes */
export function createNote(input: { title?: string; body?: string; catId?: string | null }): Promise<Note> {
  return req<Note>("/notes", { method: "POST", body: JSON.stringify(input) });
}
/** Debounce title/body edits 500ms at the call site — see FloatingNotesTasks.tsx. */
export function updateNote(id: string, changes: Partial<Pick<Note, "title" | "body" | "pinned" | "catId">>): Promise<void> {
  return req<void>(`/notes/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
}
export function deleteNote(id: string): Promise<void> {
  return req<void>(`/notes/${id}`, { method: "DELETE" });
}
